
import * as React from 'react';
import { IActionProps, ActionBase } from '../action/Action';
import { IOnExecuteProps } from '../commandSet/CommandSet';
import { Fragment } from '../../utilities/components/Fragment';
import { Signal } from '../../utilities/async/Signal';
import {
  BACKGROUND_TASK_CONTEXT_TYPES,
  IBackgroundTaskContext,
  BackgroundTaskBuffer,
  BackgroundTaskHost
} from '../backgroundTaskRunner/BackgroundTaskHost';
import { connectToContext } from '../../utilities/components/Context';
import { IBackgroundTaskQueue } from '../../models/backgroundTasks/IBackgroundTaskQueue';
import { IComponentDecorator, IDecoratedProps, IDecoratedComponentType } from '../../utilities/components/Decorators';
import { withTelemetry, ITelemetryConsumerStateProps } from '../telemetry/TelemetryProvider';
import { Engagement, IEngagementSingleSchema } from '@ms/telemetry-manager/lib/events/Engagement.event';
import { Qos, IQosStartSchema } from '@ms/telemetry-manager/lib/events/Qos.event';
import { ResultType } from '../../facets/LoggerFacets';
import { isCancel } from '../../utilities/async/CancelError';

/**
 * @public
 */
export interface IWithWizardInjectedProps {
  resolve: (result?: Promise<void>) => void;
  reject: (error: Error) => void;
  onExecuteProps?: IOnExecuteProps;
}

/**
 * @public
 */
export type IWithWizardProps = Pick<IActionProps, 'isAvailable' | 'isToggled' | 'engagement'>;

interface IWizardState {
  hasExecuted?: boolean;
}

/**
 * @public
 */
export type IDecorateWithWizardProps<TProps extends TInjectedProps, TInjectedProps = IWithWizardInjectedProps, TOwnProps = {}> =
  IDecoratedProps<TProps, TInjectedProps, TOwnProps & IWithWizardProps>;

/**
 * Wraps a component with a 'wizard' to manage its lifetime as an action.
 * A 'wizard' is a headless component which can orchestrate UX action
 * by rendering components which use layers.
 *
 * The wrapped component will receive `resolve` and `reject` callbacks via `props`
 * to facilitate completion (or failure or cancellation) of the action.
 *
 * @public
 */
export function withWizard<TInjectedProps = IWithWizardInjectedProps, TOwnProps = {}>(
  mapWizardToProps?: (wizard: IWithWizardInjectedProps, props: TOwnProps) => TInjectedProps,
  options?: {
    isBackgroundTask?: boolean;
  }
): IComponentDecorator<TInjectedProps, TOwnProps & IWithWizardProps> {
  return <P extends TInjectedProps>(
    component: React.ComponentType<P>
  ): IDecoratedComponentType<P, TInjectedProps, TOwnProps & IWithWizardProps> => {
    type IWizardProps = IDecoratedProps<P, TInjectedProps, TOwnProps & IWithWizardProps & {
      backgroundTaskQueue: IBackgroundTaskQueue;
    } & ITelemetryConsumerStateProps>;

    const Component = component;

    class Wizard extends React.PureComponent<IWizardProps, IWizardState> {
      private _backgroundTaskQueue: BackgroundTaskBuffer | undefined;

      constructor(props: IWizardProps) {
        super(props);

        this.state = {};

        if (!options || !options.isBackgroundTask) {
          this._backgroundTaskQueue = new BackgroundTaskBuffer();
        }
      }

      public render(): JSX.Element {
        const {
          isAvailable,
          isToggled
        } = this.props;

        return (
          <Fragment hidden={ true }>
            { this._backgroundTaskQueue && this.state.hasExecuted ? (
              <BackgroundTaskHost queueRef={ this._backgroundTaskQueue.queueRef } />
            ) : null }
            <ActionBase
              isAvailable={ isAvailable }
              isToggled={ isToggled }
              onExecute={ this._onExecute }
            />
          </Fragment>
        );
      }

      private _onExecute = async (onExecuteProps?: IOnExecuteProps): Promise<void> => {
        const props = this.props as IWizardProps;

        const {
          promise,
          ...signal
        } = new Signal<void>();

        const {
          isAvailable,
          isToggled,
          telemetry,
          engagement = {
            name: 'WizardAction'
          },
          backgroundTaskQueue: excludedTaskQueue,
          ...contentProps
        } = props as IWithWizardProps & Pick<IWizardProps, 'backgroundTaskQueue' | 'telemetry'>;

        const wizardInjectedProps: IWithWizardInjectedProps = {
          resolve: signal.resolve,
          reject: signal.reject,
          onExecuteProps: onExecuteProps
        };

        const propsFromWizard = mapWizardToProps ?
          mapWizardToProps(wizardInjectedProps, contentProps as TOwnProps) :
          wizardInjectedProps as {} as TInjectedProps;

        const content = (
          <Component
            { ...contentProps }
            { ...propsFromWizard }
          />
        );

        const backgroundTaskQueue = this._backgroundTaskQueue || this.props.backgroundTaskQueue;

        const {
          name
        } = engagement;

        telemetry(Engagement).withData((data: IEngagementSingleSchema) => {
          return {
            ...data,
            ...engagement
          };
        }).logData({
          name: name
        });

        const qos = new (telemetry(Qos).withData((data: IQosStartSchema) => {
          return {
            ...data
          };
        }))({ name });

        backgroundTaskQueue.enqueue({
          content: content,
          promise: promise
        });

        this.setState({
          hasExecuted: true
        });

        try {
          await promise;

          qos.end({
            resultType: ResultType.success
          });
        } catch (e) {
          if (isCancel(e)) {
            qos.end({
              resultType: ResultType.expectedFailure
            });
          } else {
            qos.end({
              resultType: ResultType.failure
            });
          }
        }
      }
    }

    const ContextWizard = connectToContext(BACKGROUND_TASK_CONTEXT_TYPES, (context: IBackgroundTaskContext):
      Pick<IWizardProps, 'backgroundTaskQueue'> => {
      return {
        backgroundTaskQueue: context.backgroundTaskQueue
      };
    })(Wizard);

    const TelemetryWizard = withTelemetry()(ContextWizard as {} as React.ComponentType<ITelemetryConsumerStateProps>);

    return TelemetryWizard as {} as IDecoratedComponentType<P, TInjectedProps, TOwnProps & IWithWizardProps>;
  };
}
